import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { COPY } from '../copy';
import { ONBOARDING_ENABLED } from '../gating/isOnboardingEnabled';
import { useSharedHiredRequisitions } from './hiredRequisitions';
import { onboardingPathFor } from './jobLink';
import styles from './OnboardingEntryCard.module.css';

interface OnboardingEntryCardProps {
  /** Defaults to the build gate; tests pass it explicitly */
  enabled?: boolean;
}

interface CardTexts {
  title: string;
  subTitle: string;
  /** The Job ID label, the form's own (`texts.requisition.jobId`, decision 107) */
  jobId: string;
}

const FALLBACK_TEXTS: CardTexts = { title: COPY.entryCard.title, subTitle: COPY.entryCard.body, jobId: COPY.entryCard.jobId };

/**
 * The card's copy is `texts.shell.homeCard`, and its Job ID label `texts.requisition.jobId`. The config isn't loaded
 * on the home page, so it loads (the onboarding chunk) once there is a card to show; the fallback copy is used only if
 * it can't be read. Behind the build gate, so a production build has no import of it (decision 42).
 */
const loadCardTexts = ONBOARDING_ENABLED
  ? (): Promise<CardTexts> =>
      import('../../index')
        .then((feature) => feature.getResolvedOnboardingConfig())
        .then(({ config }) => ({ ...config.texts.shell.homeCard, jobId: config.texts.requisition?.jobId ?? COPY.entryCard.jobId }))
  : null;

/** `undefined` until the config's copy is in, so the card never switches copy in front of the candidate */
function useCardTexts(active: boolean): CardTexts | undefined {
  const [texts, setTexts] = useState<CardTexts>();
  useEffect(() => {
    if (!active || !loadCardTexts) return undefined;
    let cancelled = false;
    loadCardTexts().then(
      (loaded) => !cancelled && setTexts(loaded),
      () => !cancelled && setTexts(FALLBACK_TEXTS),
    );
    return () => {
      cancelled = true;
    };
  }, [active]);
  return texts;
}

/**
 * The home page's way in (decision 109): one card for each requisition the signed-in candidate is hired for, with its
 * job title and Job ID, opening the form for that requisition. Nothing while the list or the copy loads, without a
 * hired requisition (or when the list can't be read), signed out, or with the feature off. A requisition the service
 * lists without its id gets no card: the form can't be saved without it.
 */
export function OnboardingEntryCard({ enabled = ONBOARDING_ENABLED }: OnboardingEntryCardProps) {
  const navigate = useNavigate();
  const hired = useSharedHiredRequisitions(enabled);
  const cards = (hired ?? []).flatMap((requisition) => {
    const path = onboardingPathFor(requisition.requisitionId, requisition.requisitionDisplayId);
    return path === undefined ? [] : [{ requisition, path }];
  });
  const texts = useCardTexts(cards.length > 0);
  if (cards.length === 0 || !texts) return null;
  return (
    <div className={styles.cards}>
      {cards.map(({ requisition, path }) => (
        <button key={requisition.requisitionDisplayId} type="button" className={styles.card} onClick={() => navigate(path)}>
          <span className={styles.bar} aria-hidden="true" />
          <span className={styles.copy}>
            <span className={styles.title}>{texts.title}</span>
            <span className={styles.job}>
              {requisition.jobTitle && <span className={styles.jobTitle}>{requisition.jobTitle}</span>}
              <span className={styles.jobId}>
                {texts.jobId} {requisition.requisitionDisplayId}
              </span>
            </span>
            <span className={styles.body}>{texts.subTitle}</span>
          </span>
          <span className={styles.arrow} aria-hidden="true">
            →
          </span>
        </button>
      ))}
    </div>
  );
}
